import { useState } from 'react';
import type { HabitCategory, HabitWeekReport } from '@contracts';
import { HeartPulse, Sparkles, BookOpen, CircleEllipsis } from '@ui/shared/icons';
import { TrendSparkline } from './TrendSparkline';

const CATEGORY_ICON: Record<HabitCategory, typeof HeartPulse> = {
  health: HeartPulse,
  self_care: Sparkles,
  study: BookOpen,
  other: CircleEllipsis,
};

const CATEGORY_TINT_VAR: Record<HabitCategory, string> = {
  health: 'var(--accent-health)',
  self_care: 'var(--accent-selfcare)',
  study: 'var(--accent-study)',
  other: 'var(--accent-other)',
};

/**
 * Habits table panel — planned vs. actual (§1.3, WR-2). One row per active
 * habit (already filtered upstream — paused/archived never reach this
 * component). Study-minutes column always renders (WR-3 layout reservation)
 * but is visually inert this round.
 */
export function HabitsTable({ habits }: { readonly habits: readonly HabitWeekReport[] }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <section
      style={{
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--layout-card-padding-desktop)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--type-heading-size)', fontWeight: 'var(--type-heading-weight)', color: 'var(--text-primary)' }}>
        This week's habits
      </h2>

      <div role="table" aria-label="Habits, planned vs actual" style={{ marginTop: 'var(--space-4)' }}>
        <div
          role="row"
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 0.8fr 0.8fr 1fr 0.9fr 1fr',
            gap: 'var(--space-3)',
            paddingBottom: 'var(--space-2)',
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          <HeaderCell>Habit</HeaderCell>
          <HeaderCell>Planned</HeaderCell>
          <HeaderCell>Actual</HeaderCell>
          <HeaderCell>Study minutes</HeaderCell>
          <HeaderCell>4-week trend</HeaderCell>
          <HeaderCell>Status</HeaderCell>
        </div>

        {habits.map((habit) => (
          <HabitRow
            key={habit.habitId}
            habit={habit}
            expanded={expandedId === habit.habitId}
            onToggle={() => setExpandedId((prev) => (prev === habit.habitId ? null : habit.habitId))}
          />
        ))}
      </div>
    </section>
  );
}

function HeaderCell({ children }: { readonly children: string }) {
  return (
    <span
      style={{
        fontSize: 'var(--type-label-size)',
        letterSpacing: 'var(--type-label-tracking)',
        textTransform: 'uppercase',
        color: 'var(--text-secondary)',
        fontWeight: 600,
      }}
    >
      {children}
    </span>
  );
}

function HabitRow({
  habit,
  expanded,
  onToggle,
}: {
  readonly habit: HabitWeekReport;
  readonly expanded: boolean;
  readonly onToggle: () => void;
}) {
  const Icon = CATEGORY_ICON[habit.category];
  const plannedLabel = formatPlanned(habit);
  const actualLabel = formatActual(habit);
  const statusLabel = habit.current.met ? 'Met' : habit.habitKind === 'build' ? 'Under plan' : 'Over allowance';

  return (
    <div role="rowgroup">
      <div
        role="row"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 0.8fr 0.8fr 1fr 0.9fr 1fr',
          gap: 'var(--space-3)',
          alignItems: 'center',
          padding: 'var(--space-3) 0',
          borderBottom: '1px solid var(--border-subtle)',
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <span
            aria-hidden="true"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `color-mix(in srgb, ${CATEGORY_TINT_VAR[habit.category]} 14%, transparent)`,
              flex: 'none',
            }}
          >
            <Icon size={16} color={CATEGORY_TINT_VAR[habit.category]} aria-hidden="true" />
          </span>
          <div>
            <div style={{ fontSize: 'var(--type-body-size)', fontWeight: 500, color: 'var(--text-primary)' }}>{habit.name}</div>
            {habit.habitKind === 'avoid' && (
              <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>keeping in check</div>
            )}
          </div>
        </div>
        <span className="num" style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
          {plannedLabel}
        </span>
        <span className="num" style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-primary)' }}>
          {actualLabel}
        </span>
        <StudyMinutesCell habit={habit} />
        <TrendSparkline trend={habit.trend} category={habit.category} />
        <span
          style={{
            fontSize: 'var(--type-caption-size)',
            fontWeight: 500,
            color: habit.current.met ? 'var(--hue-primary)' : 'var(--text-secondary)',
          }}
        >
          {statusLabel}
        </span>
      </div>
      {expanded && (
        <div
          style={{
            background: 'var(--surface-sunken)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-3) var(--space-4)',
            margin: 'var(--space-2) 0',
            fontSize: 'var(--type-caption-size)',
            color: 'var(--text-secondary)',
          }}
        >
          {habit.current.actual} logged this week against a plan of {habit.current.planned}.
        </div>
      )}
    </div>
  );
}

function StudyMinutesCell({ habit }: { readonly habit: HabitWeekReport }) {
  if (habit.category !== 'study') {
    return (
      <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-disabled)' }} aria-hidden="true">
        —
      </span>
    );
  }
  // WR-3/AC-W7: layout reservation only this round — minutesLogged is
  // always null (no session-log capture mechanism exists yet, Phase 3).
  return (
    <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
      Not tracked yet
    </span>
  );
}

function formatPlanned(habit: HabitWeekReport): string {
  if (habit.habitKind === 'build') return `${habit.current.planned}×`;
  return `≤ ${habit.current.planned}`;
}

function formatActual(habit: HabitWeekReport): string {
  if (habit.habitKind === 'build') return `${habit.current.actual}×`;
  return `${habit.current.actual} logged`;
}
